import {
    DEFAULT_VIEW,
    MAX_ZOOM,
    MIN_ZOOM,
    REFERENCE_SURFACE_DIAGONAL
} from './constants';
import type { ImageEdit, Surface, View } from './types';

/**
 * A mark size from REFERENCE pixels into this surface's, so marks stay the same
 * fraction of the sketch on every device and at every shape (see
 * REFERENCE_SURFACE_DIAGONAL). For everything a mark is MADE of — radii, font
 * sizes, stroke widths, offsets — but not for invisible hit areas, which stay a
 * comfortable size for the finger.
 */
export function markPx(surface: { width: number; height: number }) {
    const diagonal = Math.hypot(surface.width, surface.height);
    const scale = diagonal > 0 ? diagonal / REFERENCE_SURFACE_DIAGONAL : 1;
    return (value: number) => value * scale;
}

export function round(value: number, digits: number) {
    const factor = 10 ** digits;
    return Math.round(value * factor) / factor;
}

export function clampPct(value: number) {
    return Math.max(0, Math.min(100, value));
}

/**
 * Which way a leader's label hangs off the arrow's tail: the unit vector from
 * the TIP towards the TAIL — the direction pointing AWAY from whatever the arrow
 * is pointing at.
 *
 * The label always stood to the right of the tail, which put it ON the arrow for
 * every arrow drawn leftwards, and beside a vertical one where there was nothing
 * to read it against. An arrow drawn downwards now carries its label above it,
 * one drawn upwards below, one drawn to the left to the left of its tail — the
 * label continues the line rather than crossing it.
 *
 * A zero-length arrow (both ends on the same point, which is what a leader looks
 * like mid-placement) has no direction to speak of; it keeps the old rightward
 * one so the label does not sit on top of its own anchor.
 */
export function leaderLabelDirection(
    ax: number,
    ay: number,
    bx: number,
    by: number
): { x: number; y: number } {
    const dx = bx - ax;
    const dy = by - ay;
    const length = Math.hypot(dx, dy);
    if (length === 0) {
        return { x: 1, y: 0 };
    }
    return { x: dx / length, y: dy / length };
}

/**
 * Where a label's own origin goes so that the box hangs in `direction` off the
 * point it is placed at — the corner or edge FACING that point stays on it.
 *
 * Pure arithmetic on the measured box: a label sent right hangs by its left edge
 * and is centred vertically ((0, h/2)), one sent up hangs by its bottom edge and
 * is centred horizontally ((w/2, h)), and a diagonal lands in between. Konva
 * offsets shift a node's origin, so this is subtracted from where it is drawn.
 */
export function labelOriginOffset(
    direction: { x: number; y: number },
    width: number,
    height: number
): { x: number; y: number } {
    return {
        x: (width / 2) * (1 - direction.x),
        y: (height / 2) * (1 - direction.y)
    };
}

/**
 * Zoom to `nextZoom` while keeping the focal point (qx, qy) — in surface-box
 * origin space — pinned on screen. Snaps back to the identity view at zoom 1.
 */
export function applyZoom(
    prev: View,
    nextZoom: number,
    qx: number,
    qy: number
): View {
    const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
    if (zoom === 1) {
        return { ...DEFAULT_VIEW };
    }
    const ratio = zoom / prev.zoom;
    return {
        zoom,
        panX: qx - (qx - prev.panX) * ratio,
        panY: qy - (qy - prev.panY) * ratio
    };
}

/** Fit a rectangle of the given aspect (w/h) inside cw×ch, centred (letterbox). */
export function fitBox(cw: number, ch: number, aspect: number) {
    let width = cw;
    let height = cw / aspect;
    if (height > ch) {
        height = ch;
        width = ch * aspect;
    }
    return { left: (cw - width) / 2, top: (ch - height) / 2, width, height };
}

/**
 * The surface a sketch is re-rendered at: `aspect`, scaled so the LONG edge is
 * exactly `maxDimension`.
 *
 * Unlike the on-screen surface this is not fitted to a container — it IS the
 * target. Sizing the surface up front and exporting at pixelRatio 1 gives the
 * same image as drawing small and scaling up (every mark is a fraction of the
 * surface, see markPx), with no device-dependent factor left to get wrong.
 */
export function exportBox(aspect: number, maxDimension: number) {
    const safeAspect = aspect > 0 ? aspect : 1;
    const landscape = safeAspect >= 1;
    return {
        left: 0,
        top: 0,
        width: landscape ? maxDimension : Math.round(maxDimension * safeAspect),
        height: landscape ? Math.round(maxDimension / safeAspect) : maxDimension
    };
}

/** Place an image of iw×ih inside sw×sh preserving aspect (letterbox), centred. */
export function containFit(iw: number, ih: number, sw: number, sh: number) {
    const imageRatio = iw / ih;
    const surfaceRatio = sw / sh;
    let width: number;
    let height: number;
    if (imageRatio > surfaceRatio) {
        width = sw;
        height = sw / imageRatio;
    } else {
        height = sh;
        width = sh * imageRatio;
    }
    return { x: (sw - width) / 2, y: (sh - height) / 2, width, height };
}

/** A crop rectangle in source-image PIXELS: centre, size and its own angle. */
export interface CropRectPx {
    cx: number;
    cy: number;
    width: number;
    height: number;
    /** Degrees, clockwise. */
    angle: number;
}

/** The crop in image pixels — the whole image when nothing is cropped. */
export function cropRectPx(
    image: { naturalWidth: number; naturalHeight: number },
    edit: ImageEdit
): CropRectPx {
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const crop = edit.crop;
    if (!crop) {
        return { cx: iw / 2, cy: ih / 2, width: iw, height: ih, angle: 0 };
    }
    return {
        cx: (crop.x + crop.w / 2) * iw,
        cy: (crop.y + crop.h / 2) * ih,
        width: crop.w * iw,
        height: crop.h * ih,
        angle: crop.angle
    };
}

/**
 * Half the width/height of a rotated rectangle's upright bounding box. Used
 * both to keep a straightened crop inside the photo and to bound the region
 * Konva has to cache for the image filters.
 */
export function rotatedExtents(rect: {
    width: number;
    height: number;
    angle: number;
}) {
    const radians = (rect.angle * Math.PI) / 180;
    const cos = Math.abs(Math.cos(radians));
    const sin = Math.abs(Math.sin(radians));
    return {
        x: (rect.width / 2) * cos + (rect.height / 2) * sin,
        y: (rect.width / 2) * sin + (rect.height / 2) * cos
    };
}

/**
 * Bring a crop angle into (-45, 45]. Beyond that the SAME rectangle is the next
 * quarter turn with width and height swapped, and a quarter turn is
 * `imgEdit.rotate`'s business — otherwise a crop drawn on an already-rotated
 * photo stores that 90° twice and turns again on reopen.
 */
export function foldCropAngle(angle: number, width: number, height: number) {
    let folded = ((angle % 360) + 360) % 360;
    let foldedWidth = width;
    let foldedHeight = height;
    while (folded > 45) {
        folded -= 90;
        [foldedWidth, foldedHeight] = [foldedHeight, foldedWidth];
    }
    return { angle: folded, width: foldedWidth, height: foldedHeight };
}

/**
 * Keep a crop rectangle inside the photo: shrink it about its centre until its
 * corners fit, then pull the centre back in. A straightened rectangle needs
 * this even when it was dragged inside the image — turning it pushes its
 * corners out, and a corner outside the photo would be drawn as a blank wedge.
 */
export function clampCropRect(
    rect: CropRectPx,
    imageWidth: number,
    imageHeight: number
): CropRectPx {
    const extents = rotatedExtents(rect);
    const shrink = Math.min(
        1,
        extents.x > 0 ? imageWidth / 2 / extents.x : 1,
        extents.y > 0 ? imageHeight / 2 / extents.y : 1
    );
    const width = rect.width * shrink;
    const height = rect.height * shrink;
    const inner = rotatedExtents({ width, height, angle: rect.angle });
    return {
        width,
        height,
        angle: rect.angle,
        cx: Math.min(Math.max(rect.cx, inner.x), imageWidth - inner.x),
        cy: Math.min(Math.max(rect.cy, inner.y), imageHeight - inner.y)
    };
}

/**
 * Anything that maps a point — a `Konva.Transform` satisfies it structurally,
 * which keeps this module free of Konva and the crop maths testable without a
 * canvas.
 */
export interface PointMapper {
    point(point: { x: number; y: number }): { x: number; y: number };
}

/**
 * The crop a selection stands for: an axis-parallel box on SCREEN, mapped back
 * through the background node's inverse transform into image pixels.
 *
 * Read off two MAPPED EDGES — their lengths are the size, the top one's
 * direction is the angle — rather than derived from the settings that produced
 * the transform, so it holds however the chain is set: straightened, turned,
 * mirrored, zoomed.
 */
export function cropFromBox(
    inverse: PointMapper,
    box: { x: number; y: number; width: number; height: number },
    image: { naturalWidth: number; naturalHeight: number }
): { x: number; y: number; w: number; h: number; angle: number } | null {
    const topLeft = inverse.point({ x: box.x, y: box.y });
    const topRight = inverse.point({ x: box.x + box.width, y: box.y });
    const bottomLeft = inverse.point({ x: box.x, y: box.y + box.height });
    const centre = inverse.point({
        x: box.x + box.width / 2,
        y: box.y + box.height / 2
    });
    const folded = foldCropAngle(
        (Math.atan2(topRight.y - topLeft.y, topRight.x - topLeft.x) * 180) /
            Math.PI,
        Math.hypot(topRight.x - topLeft.x, topRight.y - topLeft.y),
        Math.hypot(bottomLeft.x - topLeft.x, bottomLeft.y - topLeft.y)
    );
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const rect = clampCropRect(
        {
            cx: centre.x,
            cy: centre.y,
            width: folded.width,
            height: folded.height,
            angle: folded.angle
        },
        iw,
        ih
    );
    if (rect.width < 1 || rect.height < 1) {
        return null;
    }
    // x/y/w/h describe the rectangle UPRIGHT; `angle` turns it about that same
    // centre (see the ImageEdit doc comment).
    return {
        x: (rect.cx - rect.width / 2) / iw,
        y: (rect.cy - rect.height / 2) / ih,
        w: rect.width / iw,
        h: rect.height / ih,
        angle: rect.angle
    };
}

export interface BackgroundPlacement {
    /** Where the crop lands on the surface — the photo is clipped to this. */
    box: { x: number; y: number; width: number; height: number };
    /** Node position: the crop's centre goes here. */
    x: number;
    y: number;
    /** Node offset — the crop's centre in image pixels. */
    offsetX: number;
    offsetY: number;
    /** Node rotation: undoes the crop's own angle, hanging it straight. */
    rotation: number;
    scale: number;
    /** The node draws the WHOLE photo; the clip box shows the crop of it. */
    width: number;
    height: number;
    /** Local-space region worth caching for the filters (see SketchEditor). */
    cache: { x: number; y: number; width: number; height: number };
}

/**
 * How the adjusted background fills the surface. The node carries the photo at
 * its natural size and is scaled, turned and offset so the crop rectangle lands
 * upright on `box`, which is then also the clip. That is what lets a crop be
 * STRAIGHTENED — Konva's own `crop` cuts an axis-parallel source rect and could
 * never hang a crooked one straight.
 *
 * `previewStraighten` is the crop bar's slider: degrees clockwise on SCREEN,
 * which a mirrored photo turns the other way, hence the flip sign. The 90°
 * rotation and the flips stay with the shared layer transform, not here.
 */
export function placeBackground(
    image: HTMLImageElement,
    edit: ImageEdit,
    surface: { width: number; height: number },
    previewStraighten = 0,
    showWhole = false
): BackgroundPlacement {
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const flipSign = edit.flipH !== edit.flipV ? -1 : 1;
    const base = cropRectPx(image, edit);
    const angle = base.angle - flipSign * previewStraighten;
    // Cropping shows the WHOLE photo at the crop's own angle, so the frame can
    // be let out again and not only pulled in; otherwise only the crop is on
    // screen and the rest of the picture is cut away.
    const rect = showWhole
        ? { cx: iw / 2, cy: ih / 2, width: iw, height: ih, angle }
        : { ...base, angle };
    const extents = rotatedExtents(rect);
    // Upright, the crop fills its box; turned, it is the box AROUND the turned
    // picture that has to fit, or the corners would hang off the canvas.
    const fitWidth = showWhole ? extents.x * 2 : rect.width;
    const fitHeight = showWhole ? extents.y * 2 : rect.height;
    const box = containFit(fitWidth, fitHeight, surface.width, surface.height);
    const scale = fitWidth > 0 ? box.width / fitWidth : 1;
    return {
        box,
        x: box.x + box.width / 2,
        y: box.y + box.height / 2,
        offsetX: rect.cx,
        offsetY: rect.cy,
        rotation: -rect.angle,
        scale,
        width: iw,
        height: ih,
        cache: {
            x: rect.cx - extents.x,
            y: rect.cy - extents.y,
            width: extents.x * 2,
            height: extents.y * 2
        }
    };
}

function imageToContent(
    placement: BackgroundPlacement,
    x: number,
    y: number
): { x: number; y: number } {
    // Konva's own order: translate(x,y) ∘ rotate ∘ scale ∘ translate(-offset).
    const radians = (placement.rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const dx = (x - placement.offsetX) * placement.scale;
    const dy = (y - placement.offsetY) * placement.scale;
    return {
        x: placement.x + dx * cos - dy * sin,
        y: placement.y + dx * sin + dy * cos
    };
}

/**
 * How a mark moves when the crop changes — why a new crop does not drag the
 * drawing off the picture.
 *
 * A mark is stored as a fraction of the SURFACE, and the surface IS the crop, so
 * re-cropping redefines the frame every mark is measured in. What has to stay
 * put is the mark's place in the PHOTO, so the old frame is unwound through the
 * image and the new one wound back on.
 *
 * Surface pixels in, normalized out. Between the two frames the map is a
 * similarity, so lengths take one factor and angles one addition. `position` is
 * for a point, `vector` for anything measured FROM one.
 */
export interface CropRemap {
    position(x: number, y: number): { u: number; v: number };
    vector(dx: number, dy: number): { du: number; dv: number };
    /** For a length stored as a fraction of the surface WIDTH. */
    widthFactor: number;
    /** For a mark's own scale — those follow the surface DIAGONAL (markPx). */
    markFactor: number;
    /** Degrees to add to a mark's own rotation. */
    rotation: number;
}

export function cropRemap(
    image: HTMLImageElement,
    edit: ImageEdit,
    surface: Surface,
    next: { x: number; y: number; w: number; h: number; angle: number }
): CropRemap {
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const before = cropRectPx(image, edit);
    const after = {
        cx: (next.x + next.w / 2) * iw,
        cy: (next.y + next.h / 2) * ih,
        width: next.w * iw,
        height: next.h * ih,
        angle: next.angle
    };
    // Where the old crop lies on the surface, and how many image pixels one
    // surface pixel is worth there. Read off the placement rather than assumed
    // to be the whole surface: a scene whose aspect predates its crop has the
    // photo letterboxed inside the canvas.
    const box = placeBackground(image, edit, surface).box;
    const perPixel = box.width > 0 ? before.width / box.width : 1;
    const turn = ((before.angle - after.angle) * Math.PI) / 180;
    const cos = Math.cos(turn);
    const sin = Math.sin(turn);

    const vector = (dx: number, dy: number) => {
        const ix = dx * perPixel;
        const iy = dy * perPixel;
        return {
            du: (ix * cos - iy * sin) / after.width,
            dv: (ix * sin + iy * cos) / after.height
        };
    };
    // Where the old crop's centre now sits, in the new crop's own frame.
    const shiftX = before.cx - after.cx;
    const shiftY = before.cy - after.cy;
    const afterTurn = (-after.angle * Math.PI) / 180;
    const afterCos = Math.cos(afterTurn);
    const afterSin = Math.sin(afterTurn);
    const centreU = 0.5 + (shiftX * afterCos - shiftY * afterSin) / after.width;
    const centreV =
        0.5 + (shiftX * afterSin + shiftY * afterCos) / after.height;

    return {
        position(x: number, y: number) {
            const offset = vector(
                x - (box.x + box.width / 2),
                y - (box.y + box.height / 2)
            );
            return { u: centreU + offset.du, v: centreV + offset.dv };
        },
        vector,
        widthFactor: after.width > 0 ? before.width / after.width : 1,
        markFactor:
            Math.hypot(before.width, before.height) /
            Math.max(1, Math.hypot(after.width, after.height)),
        rotation: before.angle - after.angle
    };
}

/**
 * Where the stored crop lands on the STAGE while the whole photo is shown — the
 * frame a second crop session opens with. Upright, because the photo is drawn
 * turned by the crop's own angle and that angle is undone for the crop itself:
 * what was cut stays a rectangle you can push outwards again.
 */
export function cropFrameOnStage(
    image: HTMLImageElement,
    edit: ImageEdit,
    surface: Surface,
    previewStraighten = 0
) {
    const placement = placeBackground(
        image,
        edit,
        surface,
        previewStraighten,
        true
    );
    const rect = cropRectPx(image, edit);
    const radians = (rect.angle * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const corners = [
        { x: -rect.width / 2, y: -rect.height / 2 },
        { x: rect.width / 2, y: -rect.height / 2 },
        { x: rect.width / 2, y: rect.height / 2 },
        { x: -rect.width / 2, y: rect.height / 2 }
    ]
        .map((corner) => ({
            x: rect.cx + corner.x * cos - corner.y * sin,
            y: rect.cy + corner.x * sin + corner.y * cos
        }))
        .map((corner) => imageToContent(placement, corner.x, corner.y))
        .map((corner) => contentToStage(corner.x, corner.y, edit, surface));
    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);
    return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys)
    };
}

/**
 * Where the photo lies on the STAGE, as an upright box — the frame a crop
 * session opens with.
 *
 * COMPUTED, not read off the Konva node: that one is only there once react-konva
 * has mounted it, and a crop begun before that opened with no frame at all.
 * Upright is enough — the composition turns by quarters and mirrors, and neither
 * takes an axis-parallel box off its axes.
 */
export function visibleImageBox(
    image: HTMLImageElement,
    edit: ImageEdit,
    surface: Surface,
    previewStraighten = 0,
    showWhole = false
) {
    const { box } = placeBackground(
        image,
        edit,
        surface,
        previewStraighten,
        showWhole
    );
    const corners = [
        { x: box.x, y: box.y },
        { x: box.x + box.width, y: box.y },
        { x: box.x + box.width, y: box.y + box.height },
        { x: box.x, y: box.y + box.height }
    ].map((corner) => contentToStage(corner.x, corner.y, edit, surface));
    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);
    return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys)
    };
}

export interface Composition {
    cx: number;
    cy: number;
    rotation: number;
    scaleX: number;
    scaleY: number;
}

/**
 * The rotate/flip transform shared by every content layer (image + annotations)
 * so they move as one. A 90° rotation is fit-scaled to keep the whole
 * composition inside the (unchanged) surface; flips fold into the scale sign.
 */
export function compositionTransform(
    edit: ImageEdit,
    surface: { width: number; height: number }
): Composition {
    const rotated = edit.rotate % 180 !== 0;
    const fit =
        rotated && surface.width > 0 && surface.height > 0
            ? Math.min(surface.width, surface.height) /
              Math.max(surface.width, surface.height)
            : 1;
    return {
        cx: surface.width / 2,
        cy: surface.height / 2,
        rotation: edit.rotate,
        scaleX: (edit.flipH ? -1 : 1) * fit,
        scaleY: (edit.flipV ? -1 : 1) * fit
    };
}

/**
 * A content point in stage/surface pixels (forward transform). Used during
 * render (for the label overlay), where reading the content Group ref isn't
 * allowed — so this mirrors the Group's transform with a small pure computation.
 */
export function contentToStage(
    x: number,
    y: number,
    edit: ImageEdit,
    surface: Surface
): { x: number; y: number } {
    const transform = compositionTransform(edit, surface);
    const vx = (x - transform.cx) * transform.scaleX;
    const vy = (y - transform.cy) * transform.scaleY;
    const rad = (transform.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
        x: transform.cx + (vx * cos - vy * sin),
        y: transform.cy + (vx * sin + vy * cos)
    };
}

/**
 * How a mark has to be turned to stand UPRIGHT on screen: the inverse of the
 * photo's rotate/flip, which every mark inherits from the content Group.
 *
 * A label placed on a photo turned by 90° would otherwise come out lying on its
 * side, and on a mirrored one written backwards. Only the direction is undone,
 * not the size: the fit-scale of a quarter turn shrinks every mark alike.
 *
 * Any mirror is expressed as `mirrored` (scaleX -1) plus a rotation, so the
 * stored mark needs no more than the rotation and scale it already carries.
 */
export interface Orientation {
    /** Degrees, clockwise, in [0, 360). */
    rotation: number;
    mirrored: boolean;
}

export function uprightOrientation(edit: ImageEdit): Orientation {
    const flipX = edit.flipH;
    const flipY = edit.flipV;
    const normalize = (degrees: number) => ((degrees % 360) + 360) % 360;
    if (flipX === flipY) {
        // No flip, or both — which is a half turn and no mirror at all.
        return {
            rotation: normalize((flipX ? 180 : 0) - edit.rotate),
            mirrored: false
        };
    }
    // Mirrored once: a mirror's own inverse, which reverses the turn's sense.
    return {
        rotation: normalize((flipY ? 180 : 0) + edit.rotate),
        mirrored: true
    };
}

/** A direction as seen from inside a node turned and mirrored by `orientation`. */
export function intoOrientation(
    direction: { x: number; y: number },
    orientation: Orientation
): { x: number; y: number } {
    const radians = (-orientation.rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const x = direction.x * cos - direction.y * sin;
    const y = direction.x * sin + direction.y * cos;
    return { x: orientation.mirrored ? -x : x, y };
}
